import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { map, startWith } from "rxjs/operators";
import { AppConfig } from 'src/app/app.config';
import { AuthService } from '../auth/auth.service';
import { MessageService } from '../errorHandler/message.service';
import { OrganizationalUnit } from '../models';
import { OrganizationalUnitConfig } from '../models/organizational-unit-config.model';
import { OrganizationalUnitImages } from '../models/organizational-unit-images.model';
import { PublicBrandingImage } from '../models/public-branding-image.model';
import { TokenCertificateParameters } from '../models/tokenCertificateParameters.model';
import { HotjarService } from './hotjar.service';

@Injectable({
  providedIn: 'root'
})
export class OrganizationalUnitService {
  url = AppConfig.settings.apiUrls.auth;
  urlCpp = AppConfig.settings.apiUrls.cpp;
  ousConfigs: OrganizationalUnitConfig[];
  reponse: OrganizationalUnit[];

  private readonly currentOuChangedSubject: Subject<OrganizationalUnit> = new Subject<OrganizationalUnit>();

  constructor(private readonly http: HttpClient,
    private readonly auth: AuthService,
    private readonly msjService: MessageService,
    private readonly hotjarService: HotjarService,
    @Inject(DOCUMENT) private readonly document: Document) {
    this.ousConfigs = [];

    auth.onIdentityChanged(() => {
      // Cuando cambia el usu logueado vacio lo que tengo.
      this.ousConfigs = [];
    });
  }

  /**
   * Returns an Observable that emits the current OrganizationalUnit and subsequent changes.
   *
   * The first value is obtained synchronously by reading localStorage via getCurrentOU().
   * That initial value may be undefined if no OU is stored. From then on, the stream emits new OUs when setCurrentOU is called.
   *
   * @returns Observable<OrganizationalUnit | undefined> - emits the current organizational unit or undefined if none exists.
   */
  getCurrentOu$(): Observable<OrganizationalUnit | undefined> {
    return this.currentOuChangedSubject
      .pipe(startWith(this.getCurrentOU()));
  }

  getCurrentOrChildOU(): OrganizationalUnit {
    if (localStorage.getItem("currentOU") != null) {
      // Si es una OU grupo devuelvo la primer hija que tenga disponible
      const currentOu: OrganizationalUnit = JSON.parse(localStorage.getItem("currentOU"));
      if (!currentOu.isRoot) {
        return currentOu;
      } else {
        return currentOu.childs[0];
      }
    }
  }

  getCurrentOU(): OrganizationalUnit {
    if (localStorage.getItem("currentOU") != null) {
      return JSON.parse(localStorage.getItem("currentOU"));
    }
  }

  setCurrentOU(ou: OrganizationalUnit) {
    localStorage.setItem('currentOU', JSON.stringify(ou));
    let useDocComments: boolean = ou.useDocComments ?? false;
    localStorage.setItem("useDocComments", JSON.stringify(useDocComments));
    this.hotjarService.injectHotjar(this.getCurrentOrChildOU());
    this.currentOuChangedSubject.next(ou);
  }

  async getTreeInMemory(forceRefresh = false): Promise<OrganizationalUnit[]> {

    if (localStorage.getItem("treeOu") != null && !forceRefresh) {
      return JSON.parse(localStorage.getItem("treeOu"));
    }

    const data = await firstValueFrom(this.getTree(""));
    if (data != null) {
      localStorage.setItem('treeOu', JSON.stringify(data));
      let entityId: string;
      if(localStorage.getItem("currentOU") != null) {
        entityId = JSON.parse(localStorage.getItem("currentOU")).entityId;
      }else{
        entityId = localStorage.getItem("organizationId").toString();
      }
      const org = data.find(d => d.entityId === entityId);
      let useDocComments: boolean;
      if (localStorage.getItem("isEmployee") === "true" && org?.childs?.length) {
        useDocComments = org.childs.some(c => c.useDocComments);
      } else {
        useDocComments = org?.useDocComments ?? false;
      }
      localStorage.setItem("useDocComments", JSON.stringify(useDocComments));
    }
    return data;
  }

  /**
   * Inicio (dashboard) habilitado: alguna OU habilitada del árbol de la sesión tiene useDashboard.
   * Misma condición que el feature flag del guard de la ruta. Recarga el árbol para no usar el de otra sesión.
   */
  async hasDashboardEnabled(): Promise<boolean> {
    try {
      const ous = await this.getTreeInMemory(true);
      return ous?.some(ou => ou?.enabled && ou?.useDashboard === true) ?? false;
    } catch {
      return false;
    }
  }

  getTree(name: string): Observable<OrganizationalUnit[]> {
    const params = {
      name: name,
      levelOfDetail: '4', // Complete detail
      onlyEnabled : true.toString()
    };

    return this.http
      .get<Array<OrganizationalUnit>>(`${this.url}/organizationalUnits`, { params });
  }

  getUserTree(userId: number): Observable<OrganizationalUnit[]> {
    return this.http
      .get<Array<OrganizationalUnit>>(`${this.url}/Users/${userId}/OrganizationalUnits`);
  }

  getConfigs(organizationalUnitId: number): Observable<OrganizationalUnitConfig[]> {
    return this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get<Array<any>>(`${this.url}/AdminOrganizationalUnits/Configs/` + organizationalUnitId)
      .pipe(
        map(res => this.mapResponse(res))
      );
  }

  getLawerRolId(organizationalUnitId): Observable<string> {
    return this.http
      .get<string>(`${this.urlCpp}/LawyerDocuments/Roles/` + organizationalUnitId);
  }


  getTokenParametrosCertificados(): TokenCertificateParameters {

    if (localStorage.getItem("tokenCertificateParameters") != null) {
      const resp = new TokenCertificateParameters();
      const respJson = JSON.parse(localStorage.getItem("tokenCertificateParameters"));
      resp.httpFrom = respJson.httpFrom;
      resp.httpTo = respJson.httpTo;
      resp.httpsFrom = respJson.httpsFrom;
      resp.httpsTo = respJson.httpsTo;


      return resp;
    } else {
      this.getOuConfig(['#OuConfigCertificateProviders'], Number(localStorage.getItem("organizationId"))).then(
        data => {
          const resp = new TokenCertificateParameters();
          const resultHttpFrom = data['metadataValues'].find(x => x.systemName === '#LakautEmployeeCertificateProviderHttpPortFrom');
          const resultHttpTo = data['metadataValues'].find(x => x.systemName === '#LakautEmployeeCertificateProviderHttpPortTo');
          const resultHttpsFrom = data['metadataValues'].find(x => x.systemName === '#LakautEmployeeCertificateProviderHttpsPortFrom');
          const resultHttpsTo = data['metadataValues'].find(x => x.systemName === '#LakautEmployeeCertificateProviderHttpsPortTo');

          if (resultHttpFrom != null && resultHttpFrom.value != null) {
            resp.httpFrom = Number(resultHttpFrom.value);
          }
          if (resultHttpTo != null && resultHttpTo.value != null) {
            resp.httpTo = Number(resultHttpTo.value);
          }
          if (resultHttpsFrom != null && resultHttpsFrom.value != null) {
            resp.httpsFrom = Number(resultHttpsFrom.value);
          }
          if (resultHttpsTo != null && resultHttpsTo.value != null) {
            resp.httpsTo = Number(resultHttpsTo.value);
          }
          localStorage.setItem("tokenCertificateParameters", JSON.stringify(resp));


          return resp;
        }
      );
    }

    return new TokenCertificateParameters();
  }

  saveLogo(id: number, logo: File) {
    const formData: FormData = new FormData();
    formData.append(logo.name, logo, 'logo_' + logo.name);

      return this.http.patch(
      `${this.urlCpp}/OrganizationalUnits/ChangeLogo/` + id, formData);
  }

  saveTheme(ou: OrganizationalUnit) {
    return this.http.patch(
      `${this.urlCpp}/OrganizationalUnits/ChangeTheme/`, ou);
  }

  saveImages(ou: OrganizationalUnit,image: File[], subdomain){
    const formData = this.formDataFile(image);
    formData.append('ou', JSON.stringify(ou));
    formData.append("subDomain", JSON.stringify(subdomain));
      return this.http.post(
      `${this.url}/AdminOrganizationalUnits/Images/`, formData);
  }

  private formDataFile(files: File[]) {
    const formData: FormData = new FormData();

    if (files && files.length > 0) {
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        formData.append(file.name, file, file.name);
      }
    }
    return formData;
  }


  getImagesByOuId(id: number): Observable<OrganizationalUnitImages[]> {
    return this.http.put<OrganizationalUnitImages[]>(
      `${this.url}/adminOrganizationalUnits/getImages/` + id, id)
      .pipe(
        map(res => this.mapImagesResponse(res))
      );
  }

  getImagesBySubDomain(subdomain: string): Observable<PublicBrandingImage[]> {
    return this.http.put<PublicBrandingImage[]>(
      `${this.url}/adminOrganizationalUnits/GetImagesBySubdomain/${subdomain}`, subdomain)
      .pipe(
        map(res => this.mapPublicBrandingResponse(res))
      );
  }

  deleteImage(ou: OrganizationalUnit,fileId: string) {
    return this.http
      .put<unknown>(`${this.url}/AdminOrganizationalUnits/DeleteImage/${ou.id}/${fileId}`, fileId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapResponse(response: Array<any>): OrganizationalUnitConfig[] {
    const result = [];
    if (!response || response.length === 0) {
      return result;
    }

    response.forEach(ouc => {
      result.push(new OrganizationalUnitConfig(ouc.id, ouc.name, ouc.configName, ouc.isRoot, ouc.metatadataValues));
    });

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapImagesResponse(response: Array<any>): OrganizationalUnitImages[] {
    const result = [];
    if (!response || response.length === 0) {
      return result;
    }

    response.forEach(ouc => {
      result.push(new OrganizationalUnitImages(ouc.id, ouc.isLogo, ouc.base64File, ouc.theme, ouc.organizationalUnitId));
    });

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapPublicBrandingResponse(response: Array<any>): PublicBrandingImage[] {
    const result = [];
    if (!response || response.length === 0) {
      return result;
    }

    response.forEach(ouc => {
      result.push(new PublicBrandingImage(ouc.isLogo, ouc.base64File));
    });

    return result;
  }

  /*
   * Devuelve la configuracion segun la key pasada
   * En orden de prioridad, si existe la primer key, la primera, sino la segunda.
   * Para la ou por parametro
   */
  async getOuConfig(keys: string[], ou: number): Promise<OrganizationalUnitConfig> {
    if (this.ousConfigs.length === 0) {
      this.ousConfigs = await firstValueFrom(this.getConfigs(ou));
    }

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      let configs = this.ousConfigs.filter(c => c.configName === key && c.organizationalUnitId == ou);

      if (configs.length > 0) {
        return configs[0];
      }

      configs = this.ousConfigs.filter(c => c.configName === key && c.isRoot);

      if (configs.length > 0) {
        return configs[0];
      }
    }

    return null;
  }

  getTreeOu() {
    return JSON.parse(localStorage.getItem("treeOu"));
  }

  setTheme(forceRefresh = false) {
    const ouId = +localStorage.getItem("organizationId");
    this.getTreeInMemory(forceRefresh).then(
      ous => {
        const myOu = ous.find(ou => ou.id == ouId);
        if (myOu.humanageTheme && myOu.humanageTheme.length > 0) {
          this.document.body.classList.add(myOu.humanageTheme);
        }
        if (myOu.darkMode) {
          this.document.body.classList.add('dark');
        }
        localStorage.setItem(`humanageTheme_${ouId}`, myOu.humanageTheme);
      }
    );
  }

  getOUTheme() {
    const ouId = +localStorage.getItem("organizationId");
    return localStorage.getItem(`humanageTheme_${ouId}`);
  }

  getOuRootTree(forceRefresh = false) {
    if (!forceRefresh) {
      const ous: OrganizationalUnit[] = JSON.parse(localStorage.getItem(`rootTree`));
      if (ous) {
        return of(ous);
      }
    }

    const params = {
      enabled: true.toString()
    };

    return this.http.get<OrganizationalUnit[]>(`${this.urlCpp}/OrganizationalUnits/rootTree`, { params }).pipe(map(res => {
      localStorage.setItem(`rootTree`, JSON.stringify(res));
      return res;
    }));
  }


}
